/**
 * ControlCenter — macOS-style quick settings panel
 * Toggles: Theme, Mic, TTS, Dock position, Wallpaper presets
 * Rule 1: MAX 200 lines | Rule 34: AI integrated (dispatches OS events)
 */
import { Mic, MicOff, Moon, MoonStar, Move, Palette, Sun, Volume2, VolumeX, X } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useFocusMode, type FocusMode } from '../../../contexts/FocusModeContext'
import { useOSTheme } from '../OSThemeContext'

interface Props {
  open: boolean
  onClose: () => void
}

const WALLPAPERS = [
  { id: 'midnight', label: 'Midnight', color: '#0a0a1a' },
  { id: 'ocean', label: 'Ocean', color: '#0d2137' },
  { id: 'forest', label: 'Forest', color: '#0d1f0d' },
  { id: 'slate', label: 'Slate', color: '#1a1a2e' },
  { id: 'cosmos', label: 'Cosmos', color: '#0d0d2b' },
  { id: 'ember', label: 'Ember', color: '#1a0a00' },
  { id: 'void', label: 'Void', color: '#000000' },
]

const DOCK_POSITIONS = ['bottom', 'top', 'left', 'right'] as const

export function ControlCenter({ open, onClose }: Props) {
  const { theme, toggle } = useOSTheme()
  const { enabled: focusEnabled, mode: focusMode, toggle: toggleFocus, setMode: setFocusMode } = useFocusMode()
  const [micOn, setMicOn] = useState(false)
  const [ttsOn, setTtsOn] = useState(false)
  const [currentWallpaper, setCurrentWallpaper] = useState(localStorage.getItem('os-wallpaper') ?? 'midnight')
  const [dockPos, setDockPos] = useState<string>(localStorage.getItem('os-dock-position') ?? 'bottom')
  const panelRef = useRef<HTMLDivElement>(null)

  // Sync mic/tts state from localStorage
  useEffect(() => {
    setMicOn(localStorage.getItem('os-mic') === 'on')
    setTtsOn(localStorage.getItem('os-tts') === 'on')
  }, [open])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose()
    }
    setTimeout(() => document.addEventListener('mousedown', handler), 100)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, onClose])

  const toggleMic = () => {
    const next = !micOn
    setMicOn(next)
    localStorage.setItem('os-mic', next ? 'on' : 'off')
    window.dispatchEvent(new CustomEvent('runai:toggle-mic', { detail: next ? 'on' : 'off' }))
  }

  const toggleTTS = () => {
    const next = !ttsOn
    setTtsOn(next)
    localStorage.setItem('os-tts', next ? 'on' : 'off')
    window.dispatchEvent(new CustomEvent('runai:toggle-tts', { detail: next ? 'on' : 'off' }))
  }

  const changeWallpaper = (preset: string) => {
    setCurrentWallpaper(preset)
    localStorage.setItem('os-wallpaper', preset)
    window.dispatchEvent(new CustomEvent('runai:wallpaper-change', { detail: preset }))
  }

  const changeDock = (pos: string) => {
    setDockPos(pos)
    localStorage.setItem('os-dock-position', pos)
    window.dispatchEvent(new CustomEvent('runai:dock-position', { detail: pos }))
  }

  if (!open) return null

  const Toggle = ({ on, onClick, icon, label, color }: { on: boolean; onClick: () => void; icon: React.ReactNode; label: string; color: string }) => (
    <button
      onClick={onClick}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        padding: '12px 8px', borderRadius: 12, border: 'none', cursor: 'pointer',
        background: on ? `${color}20` : 'var(--os-input-bg)',
        outline: on ? `1.5px solid ${color}` : '1.5px solid transparent',
        transition: 'all 0.15s', flex: 1,
      }}
    >
      <div style={{ color: on ? color : 'var(--os-muted)' }}>{icon}</div>
      <span style={{ fontSize: 9, color: on ? color : 'var(--os-muted)', fontWeight: 600 }}>{label}</span>
    </button>
  )

  return (
    <div
      ref={panelRef}
      style={{
        position: 'fixed',
        top: 32,
        right: 8,
        width: 280,
        background: 'var(--os-surface)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        border: '1px solid var(--os-border)',
        borderRadius: 14,
        boxShadow: 'var(--shadow-window)',
        zIndex: 9000,
        padding: 16,
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
        <span style={{ fontSize: 13, fontWeight: 700, flex: 1 }}>Control Center</span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--os-muted)', padding: 2 }}>
          <X size={14} />
        </button>
      </div>

      {/* Quick Toggles */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
        <Toggle on={theme === 'dark'} onClick={toggle} icon={theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />} label={theme === 'dark' ? 'Dark' : 'Light'} color="#6366f1" />
        <Toggle on={micOn} onClick={toggleMic} icon={micOn ? <Mic size={18} /> : <MicOff size={18} />} label="Mic" color="#10b981" />
        <Toggle on={ttsOn} onClick={toggleTTS} icon={ttsOn ? <Volume2 size={18} /> : <VolumeX size={18} />} label="TTS" color="#a855f7" />
      </div>

      {/* Focus Mode */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <MoonStar size={12} color="var(--os-muted)" />
          <span style={{ fontSize: 11, color: 'var(--os-muted)', fontWeight: 600 }}>Focus Mode</span>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Toggle on={focusEnabled} onClick={toggleFocus} icon={<MoonStar size={18} />} label={focusEnabled ? 'On' : 'Off'} color="#f59e0b" />
          <select
            value={focusMode}
            onChange={e => setFocusMode(e.target.value as FocusMode)}
            style={{
              flex: 1, fontSize: 10, padding: '6px 8px', borderRadius: 6,
              background: 'var(--os-input-bg)', color: 'var(--os-text)',
              border: '1px solid var(--os-border)', cursor: 'pointer',
            }}
          >
            <option value="work">Work</option>
            <option value="sleep">Sleep</option>
            <option value="personal">Personal</option>
            <option value="custom">Custom</option>
          </select>
        </div>
      </div>

      {/* Wallpaper */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <Palette size={12} color="var(--os-muted)" />
          <span style={{ fontSize: 11, color: 'var(--os-muted)', fontWeight: 600 }}>Wallpaper</span>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {WALLPAPERS.map(w => (
            <button
              key={w.id}
              onClick={() => changeWallpaper(w.id)}
              title={w.label}
              style={{
                width: 32, height: 32, borderRadius: 8, background: w.color, border: 'none', cursor: 'pointer',
                outline: currentWallpaper === w.id ? '2px solid var(--os-accent)' : '2px solid transparent',
                outlineOffset: 2, transition: 'outline 0.15s',
              }}
            />
          ))}
        </div>
      </div>

      {/* Dock Position */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
          <Move size={12} color="var(--os-muted)" />
          <span style={{ fontSize: 11, color: 'var(--os-muted)', fontWeight: 600 }}>Dock Position</span>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {DOCK_POSITIONS.map(pos => (
            <button
              key={pos}
              onClick={() => changeDock(pos)}
              style={{
                flex: 1, padding: '5px 0', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 10, fontWeight: 600,
                background: dockPos === pos ? 'var(--os-accent)' : 'var(--os-input-bg)',
                color: dockPos === pos ? '#fff' : 'var(--os-muted)',
                transition: 'all 0.15s', textTransform: 'capitalize',
              }}
            >
              {pos}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ControlCenter
